/**
 * The day's orders.
 *
 * WHAT PEOPLE COME HERE FOR
 * -------------------------
 * Almost always one order, not a list: a customer is at the counter with a
 * complaint, or wants their bill again. So the search is the first thing, it
 * matches an order number, and tapping a row opens the same bill the customer
 * was handed — with the same refund button on it.
 *
 * Defaults to today. A restaurant's questions are nearly all about today, and
 * loading a year of orders to answer them would be slow and useless.
 */
import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { formatPKR } from '@foodrun/shared';

import { api, withQuery } from '../../lib/api.js';
import { useAuth } from '../../stores/auth.js';
import { BillSheet } from '../pos/BillSheet.jsx';
import { statusLook, readableType, orderTime } from './orderStatus.js';
import { useTradingDayState } from '../pos/useBusinessDay.js';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { Button } from '../../components/ui/Button.jsx';

const PAGE = 25;

export default function OrdersPage() {
  const can = useAuth((s) => s.can);
  // Who may put a bill right after the fact: the owner and managers, nobody else.
  const admin = useAuth((s) => s.hasRole('owner', 'super_admin', 'branch_manager'));

  // Trading days, not the calendar's: a bill rung up at 1 am is listed under
  // the night before. One day by default; either end can be moved to see a run.
  const [from, setFrom, tradingDay] = useTradingDayState();
  const [to, setTo] = useTradingDayState();
  const [type, setType] = useState('');
  const [paidBy, setPaidBy] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [billFor, setBillFor] = useState(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['orders', from, to, type, paidBy, page],
    queryFn: () =>
      api.get(
        withQuery('/pos/orders', {
          from,
          to,
          order_type: type,
          paid_by: paidBy,
          limit: PAGE,
          offset: page * PAGE,
        })
      ),
    // Keep the current page on screen while the next one loads, so paging does
    // not blink through an empty table.
    placeholderData: keepPreviousData,
  });

  const orders = data?.orders ?? [];
  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE));

  // Searching filters the page in hand. The endpoint has no search of its own,
  // and pretending otherwise — by filtering a page and calling it a search —
  // would quietly miss orders on every other page.
  const term = search.trim().toLowerCase();
  const shown = term
    ? orders.filter(
        (o) =>
          o.order_number?.toLowerCase().includes(term) ||
          o.customer_name?.toLowerCase().includes(term) ||
          o.customer_phone?.includes(term)
      )
    : orders;

  function change(setter) {
    return (value) => {
      setter(value);
      setPage(0);
    };
  }

  return (
    <div className="mx-auto max-w-5xl p-4">
      <div className="flex flex-wrap items-end gap-2">
        <label className="min-w-0">
          <span className="mb-1.5 block text-sm font-medium text-ink-2">From (trading day)</span>
          <input
            type="date"
            value={from}
            max={to}
            onChange={(e) => e.target.value && change(setFrom)(e.target.value)}
            className="h-12 rounded-lg border border-rule bg-surface-3 px-3 text-ink text-[15px] focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
          />
        </label>
        <label className="min-w-0">
          <span className="mb-1.5 block text-sm font-medium text-ink-2">
            To{to === tradingDay && from === to ? ' (open now)' : ''}
          </span>
          <input
            type="date"
            value={to}
            min={from}
            onChange={(e) => e.target.value && change(setTo)(e.target.value)}
            className="h-12 rounded-lg border border-rule bg-surface-3 px-3 text-ink text-[15px] focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
          />
        </label>

        <label className="min-w-0">
          <span className="mb-1.5 block text-sm font-medium text-ink-2">Type</span>
          <select
            value={type}
            onChange={(e) => change(setType)(e.target.value)}
            className="h-12 rounded-lg border border-rule bg-surface-3 px-3 text-[15px] focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
          >
            <option value="">All</option>
            <option value="dine_in">Dine in</option>
            <option value="takeaway">Takeaway</option>
            <option value="delivery">Delivery</option>
            <option value="foodpanda">Foodpanda</option>
          </select>
        </label>

        <label className="min-w-0">
          <span className="mb-1.5 block text-sm font-medium text-ink-2">Paid by</span>
          <select
            value={paidBy}
            onChange={(e) => change(setPaidBy)(e.target.value)}
            aria-label="Paid by"
            className="h-12 rounded-lg border border-rule bg-surface-3 px-3 text-[15px] focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
          >
            <option value="">All</option>
            <option value="cash">Cash</option>
            <option value="card">Card</option>
            <option value="online">Online</option>
            <option value="account">On account</option>
            <option value="foodpanda">Foodpanda</option>
            <option value="unpaid">Unpaid</option>
          </select>
        </label>

        <label className="min-w-[12rem] flex-1">
          <span className="mb-1.5 block text-sm font-medium text-ink-2">Find on this page</span>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Order number, name or phone"
            className="h-12 w-full rounded-lg border border-rule bg-surface-3 px-3.5 text-ink text-[15px] placeholder:text-ink-3 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
          />
        </label>
      </div>

      {isLoading ? (
        <div className="flex h-48 items-center justify-center">
          <Spinner label="Loading orders" />
        </div>
      ) : isError ? (
        <p className="py-16 text-center text-sm text-ink-2">
          Could not load orders. Check the branch computer is running.
        </p>
      ) : shown.length === 0 ? (
        <p className="py-16 text-center text-sm text-ink-2">
          {term ? `Nothing matching “${search}” on this page.` : 'No orders on this day.'}
        </p>
      ) : (
        <div className="mt-4 overflow-x-auto rounded-xl border border-rule bg-surface">
          <table className="w-full text-sm">
            <caption className="sr-only">Orders, most recent first</caption>
            <thead>
              <tr className="border-b border-rule text-left text-xs uppercase tracking-wide text-ink-3">
                <th className="p-3 font-medium">#</th>
                <th className="p-3 font-medium">Order</th>
                <th className="p-3 font-medium">Time</th>
                <th className="p-3 font-medium">Type</th>
                <th className="p-3 font-medium">Items</th>
                <th className="p-3 font-medium">Status</th>
                <th className="p-3 font-medium">Paid by</th>
                <th className="p-3 text-right font-medium">Total</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((order) => {
                const look = statusLook(order.is_void ? 'cancelled' : order.status);

                return (
                  <tr
                    key={order.id}
                    onClick={() => setBillFor(order.id)}
                    tabIndex={0}
                    role="button"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setBillFor(order.id);
                      }
                    }}
                    className="cursor-pointer border-b border-rule last:border-0 hover:bg-surface-3"
                  >
                    {/* The day's bill serial, bold — the number the counter calls out. */}
                    <td className="p-3 font-bold tabular-nums text-ink">{order.daily_serial ?? '—'}</td>
                    <td className="p-3 font-medium text-ink">{order.order_number}</td>
                    <td className="p-3 tabular-nums text-ink-2">{orderTime(order.created_at)}</td>
                    <td className="p-3 text-ink-2">{readableType(order.order_type)}</td>
                    <td className="p-3 tabular-nums text-ink-2">{order.item_count ?? '—'}</td>
                    <td className="p-3">
                      <span
                        className={`inline-block rounded-full px-2.5 py-1 text-xs font-medium ${look.tone}`}
                      >
                        {look.label}
                      </span>
                    </td>
                    <td className="p-3 text-ink-2">{paidByLabel(order)}</td>
                    <td className="p-3 text-right font-medium tabular-nums text-ink">
                      {formatPKR(order.total_amount)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {total > PAGE && (
        <div className="mt-3 flex items-center justify-between">
          <p className="text-sm text-ink-2">
            {page * PAGE + 1}–{Math.min((page + 1) * PAGE, total)} of {total}
          </p>

          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= pages - 1}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {billFor && (
        <BillSheet
          orderId={billFor}
          canRefund={can('pos.refund.pos')}
          admin={admin}
          onClose={() => setBillFor(null)}
        />
      )}
    </div>
  );
}

/** How a bill was paid, in a word or two: "Cash", "Card + cash", "On account", "Foodpanda", "Unpaid". */
export function paidByLabel(order) {
  const NAMES = { cash: 'Cash', card: 'Card', online: 'Online', bank_transfer: 'Bank' };
  const parts = String(order.paid_methods || '').split('+').filter(Boolean).map((m) => NAMES[m] ?? m);
  if (order.via_foodpanda) parts.push('Foodpanda');
  if (order.on_account) parts.push('On account');
  if (parts.length) return parts.join(' + ');
  if (order.is_void || order.status === 'cancelled') return '—';
  return order.payment_status === 'paid' ? 'Paid' : 'Unpaid';
}
